import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Progress } from "@/components/ui/progress";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import axios from "axios";
import { 
  X, 
  ChevronLeft, 
  ChevronRight, 
  Heart, 
  Eye,
  MapPin,
  Pause,
  Play,
  MoreHorizontal,
  Archive,
  Trash2
} from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const EMOJI_REACTIONS = ["❤️", "🔥", "👏", "😂", "😮"];

const StoryViewer = ({ open, onClose, users, initialUserIndex = 0, currentUserId }) => {
  const navigate = useNavigate();
  const [currentUserIdx, setCurrentUserIdx] = useState(initialUserIndex);
  const [currentStoryIdx, setCurrentStoryIdx] = useState(0);
  const [progress, setProgress] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [showReactions, setShowReactions] = useState(false);
  const [viewersList, setViewersList] = useState([]);
  const [showViewers, setShowViewers] = useState(false);
  const videoRef = useRef(null);
  const timerRef = useRef(null);

  // Dialog açıldığında state'leri sıfırla
  useEffect(() => {
    if (open) {
      setCurrentUserIdx(initialUserIndex);
      setCurrentStoryIdx(0);
      setProgress(0);
      setIsPaused(false);
      setShowReactions(false);
      setShowViewers(false);
    }
  }, [open, initialUserIndex]);

  const currentUser = users[currentUserIdx];
  const currentStory = currentUser?.stories?.[currentStoryIdx];
  const STORY_DURATION = 5000; // 5 saniye (fotoğraf için)

  // Hikaye değiştiğinde görüntüleme kaydet
  useEffect(() => {
    if (currentStory && currentStory.userId !== currentUserId) {
      markAsViewed();
    }
  }, [currentStory?.id]);

  // Progress timer
  useEffect(() => {
    if (!currentStory || isPaused) return;

    const duration = currentStory.mediaType === "video" ? 15000 : STORY_DURATION;
    const interval = 50;
    let elapsed = 0;

    timerRef.current = setInterval(() => {
      elapsed += interval;
      setProgress((elapsed / duration) * 100);

      if (elapsed >= duration) {
        goToNext();
      }
    }, interval);

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [currentStory?.id, isPaused]);

  const markAsViewed = async () => {
    try {
      await axios.post(`${API}/stories/${currentStory.id}/view`);
    } catch (error) {
      console.error("View error:", error);
    }
  };

  const goToNext = () => {
    setProgress(0);
    if (currentStoryIdx < currentUser.stories.length - 1) {
      setCurrentStoryIdx(prev => prev + 1);
    } else if (currentUserIdx < users.length - 1) {
      setCurrentUserIdx(prev => prev + 1);
      setCurrentStoryIdx(0);
    } else {
      onClose();
    }
  };

  const goToPrev = () => {
    setProgress(0);
    if (currentStoryIdx > 0) {
      setCurrentStoryIdx(prev => prev - 1);
    } else if (currentUserIdx > 0) {
      setCurrentUserIdx(prev => prev - 1);
      const prevUser = users[currentUserIdx - 1];
      setCurrentStoryIdx(prevUser.stories.length - 1);
    }
  };

  const handleReaction = async (emoji) => {
    try {
      await axios.post(`${API}/stories/${currentStory.id}/react`, { emoji });
      toast.success(`${emoji} tepkisi gönderildi!`);
      setShowReactions(false);
    } catch (error) {
      toast.error("Tepki gönderilemedi");
    }
  };

  const handleArchive = async () => {
    try {
      await axios.post(`${API}/stories/${currentStory.id}/archive`);
      toast.success("Hikaye arşive taşındı!");
      goToNext(); // Sonraki hikayeye geç
    } catch (error) {
      toast.error("Arşivlenemedi");
    }
  };

  const handleDelete = async () => {
    if (!confirm("Bu hikayeyi silmek istediğinize emin misiniz?")) return;
    try {
      await axios.delete(`${API}/stories/${currentStory.id}`);
      toast.success("Hikaye silindi!");
      goToNext(); // Sonraki hikayeye geç
    } catch (error) {
      toast.error("Silinemedi");
    }
  };

  const formatTime = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diff = Math.floor((now - date) / 1000 / 60);
    if (diff < 1) return "Az önce";
    if (diff < 60) return `${diff}dk`;
    if (diff < 1440) return `${Math.floor(diff / 60)}sa`;
    return `${Math.floor(diff / 1440)}g`;
  };

  if (!currentStory) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md w-full p-0 bg-black border-0">
        <div className="relative w-full aspect-[9/16] max-h-[80vh]">
          {/* Progress Bars */}
          <div className="absolute top-0 left-0 right-0 z-50 flex gap-1 p-2">
            {currentUser.stories.map((_, idx) => (
              <div key={idx} className="flex-1 h-1 bg-white/30 rounded-full overflow-hidden">
                <div
                  className="h-full bg-white transition-all duration-100"
                  style={{
                    width: idx < currentStoryIdx ? "100%" : 
                           idx === currentStoryIdx ? `${progress}%` : "0%"
                  }}
                />
              </div>
            ))}
          </div>

          {/* Header */}
          <div className="absolute top-4 left-0 right-0 z-40 flex items-center justify-between px-4 pt-2">
            <button 
              className="flex items-center gap-3 hover:opacity-80 transition-opacity"
              onClick={() => {
                onClose();
                navigate(`/profile/${currentUser.userId}`);
              }}
            >
              <Avatar className="h-10 w-10 ring-2 ring-white">
                <AvatarImage src={currentUser.userAvatar} />
                <AvatarFallback>{currentUser.userName?.[0]}</AvatarFallback>
              </Avatar>
              <div className="text-left">
                <p className="text-white font-semibold text-sm">{currentUser.userName}</p>
                <p className="text-white/70 text-xs">{formatTime(currentStory.createdAt)}</p>
              </div>
            </button>
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="icon"
                className="text-white hover:bg-white/20"
                onClick={() => setIsPaused(!isPaused)}
              >
                {isPaused ? <Play className="h-5 w-5" /> : <Pause className="h-5 w-5" />}
              </Button>
              
              {/* Kendi hikayemiz için 3 nokta menüsü */}
              {currentStory.userId === currentUserId && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="text-white hover:bg-white/20"
                    >
                      <MoreHorizontal className="h-5 w-5" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={handleArchive}>
                      <Archive className="h-4 w-4 mr-2" />
                      Arşivle
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={handleDelete} className="text-red-600">
                      <Trash2 className="h-4 w-4 mr-2" />
                      Sil
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
              
              <Button
                variant="ghost"
                size="icon"
                className="text-white hover:bg-white/20"
                onClick={onClose}
              >
                <X className="h-5 w-5" />
              </Button>
            </div>
          </div>

          {/* Story Content */}
          <div 
            className="flex-1 flex items-center justify-center"
            onClick={(e) => {
              const rect = e.currentTarget.getBoundingClientRect();
              const x = e.clientX - rect.left;
              if (x < rect.width / 3) goToPrev();
              else if (x > rect.width * 2 / 3) goToNext();
              else setIsPaused(!isPaused);
            }}
          >
            {currentStory.mediaType === "video" ? (
              <video
                ref={videoRef}
                src={currentStory.mediaUrl}
                className="max-w-full max-h-full object-contain"
                autoPlay
                muted={false}
                playsInline
                onEnded={goToNext}
              />
            ) : (
              <img
                src={currentStory.mediaUrl}
                alt="Story"
                className="max-w-full max-h-full object-contain"
              />
            )}
          </div>

          {/* Story Overlays Container - Bottom area */}
          <div className="absolute bottom-24 left-4 right-4 z-30 space-y-2">
            {/* Mentions */}
            {currentStory.mentions?.length > 0 && (
              <div className="flex flex-wrap gap-1">
                {currentStory.mentions.map((mention, idx) => (
                  <button 
                    key={idx} 
                    onClick={() => {
                      onClose();
                      navigate(`/profile/${mention.userId}`);
                    }}
                    className="bg-blue-600/80 hover:bg-blue-600 text-white px-2 py-0.5 rounded text-xs transition-colors"
                  >
                    @{mention.userName}
                  </button>
                ))}
              </div>
            )}

            {/* Check-in Badge */}
            {currentStory.venueCheckIn && (
              <div>
                <button
                  onClick={() => {
                    onClose();
                    navigate(`/venue/${currentStory.venueCheckIn.venueId}/stories`);
                  }}
                  className="inline-flex items-center gap-1 bg-emerald-600/80 hover:bg-emerald-600 text-white px-3 py-1 rounded-full text-sm transition-colors"
                >
                  <MapPin className="h-3 w-3" />
                  {currentStory.venueCheckIn.venueName}
                </button>
              </div>
            )}

            {/* Caption */}
            {currentStory.caption && (
              <div className="bg-black/30 px-3 py-2 rounded-lg">
                <p className="text-white text-sm">{currentStory.caption}</p>
              </div>
            )}
          </div>

          {/* Bottom Actions */}
          <div className="absolute bottom-0 left-0 right-0 z-40 p-4 pb-6 bg-gradient-to-t from-black/90 via-black/60 to-transparent">
            <div className="flex items-center justify-between">
              {/* Views (sadece kendi hikayesi için) */}
              {currentStory.userId === currentUserId ? (
                <Button
                  variant="ghost"
                  className="text-white hover:bg-white/20 border border-white/30"
                  onClick={() => setShowViewers(!showViewers)}
                >
                  <Eye className="h-5 w-5 mr-2" />
                  {currentStory.views?.length || 0} görüntüleme
                </Button>
              ) : (
                <div />
              )}

              {/* Reactions */}
              {currentStory.userId !== currentUserId && (
                <div className="flex items-center gap-2">
                  {showReactions ? (
                    <div className="flex items-center gap-1 bg-white/20 rounded-full px-2 py-1">
                      {EMOJI_REACTIONS.map(emoji => (
                        <button
                          key={emoji}
                          onClick={() => handleReaction(emoji)}
                          className="text-2xl hover:scale-125 transition-transform"
                        >
                          {emoji}
                        </button>
                      ))}
                    </div>
                  ) : (
                    <>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="text-white hover:bg-white/20"
                        onClick={() => setShowReactions(true)}
                      >
                        <Heart className="h-6 w-6" />
                      </Button>
                    </>
                  )}
                </div>
              )}
            </div>

            {/* Viewers List */}
            {showViewers && currentStory.views?.length > 0 && (
              <div className="mt-3 bg-white/10 rounded-lg p-3 max-h-40 overflow-y-auto">
                <p className="text-white/70 text-xs mb-2">Görüntüleyenler</p>
                {currentStory.views.map((view, idx) => (
                  <button 
                    key={idx} 
                    className="w-full flex items-center gap-2 py-2 hover:bg-white/10 rounded transition-colors"
                    onClick={() => {
                      onClose();
                      navigate(`/profile/${view.userId}`);
                    }}
                  >
                    <Avatar className="h-8 w-8">
                      <AvatarImage src={view.userAvatar} />
                      <AvatarFallback className="bg-white/30 text-white text-xs">
                        {view.userName?.[0]}
                      </AvatarFallback>
                    </Avatar>
                    <span className="text-white text-sm flex-1 text-left">{view.userName}</span>
                    <span className="text-white/50 text-xs">{formatTime(view.viewedAt)}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Navigation Arrows */}
          {currentUserIdx > 0 && (
            <button
              className="absolute left-2 top-1/2 -translate-y-1/2 z-30 bg-black/30 rounded-full p-1"
              onClick={goToPrev}
            >
              <ChevronLeft className="h-6 w-6 text-white" />
            </button>
          )}
          {currentUserIdx < users.length - 1 && (
            <button
              className="absolute right-2 top-1/2 -translate-y-1/2 z-30 bg-black/30 rounded-full p-1"
              onClick={goToNext}
            >
              <ChevronRight className="h-6 w-6 text-white" />
            </button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default StoryViewer;
